import React, { useEffect, useState, useMemo, type ChangeEvent, useCallback } from "react";
import moment from "moment";

import {
  Constants,
  HerdingDeductions,
  HERDING_COURSE_D_ELEMENTS,
  HERDING_COURSE_D_OPTIONAL_ELEMENT_COUNT,
  HERDING_COURSE_D_OPTIONAL_ELEMENT_POINTS,
  IHerdingDeductionExercise,
} from "@eagerdog/constants";
import { IShow, IScore, IHerdingScore, IHerdingElementDeduction } from "@eagerdog/interfaces";

import { apiService } from "src/services";

import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import Input from "src/components/Input/Input";
import { TimeInput } from "src/components/TimeInput";

import { calculateNewPlacement } from "../utils/getScorePlacement";

import 'react-time-picker/dist/TimePicker.css';
import 'react-clock/dist/Clock.css';
import "./HerdingResults.scss";

interface IProps {
  show?: IShow,
  showType: string,
  result: IScore,
  onChange(result: IHerdingScore): void
}

interface IHerdingDeductionRow {
  id: string;
  label: string;
  maxPoints?: number;
  amount: string;
  editableLabel?: boolean;
}

const normalizeDecimalInput = (value: string): string => {
  let normalized = value.replace(/[^0-9.]/g, "");
  const firstDotIndex = normalized.indexOf(".");
  if (firstDotIndex !== -1) {
    normalized =
      normalized.slice(0, firstDotIndex + 1) +
      normalized.slice(firstDotIndex + 1).replace(/\./g, "");
  }
  if (normalized === ".") {
    normalized = "0.";
  }
  return normalized;
}

const buildExerciseRows = (result: IScore): IHerdingDeductionRow[] => {
  const dogClass = result.dog_class;
  const course = dogClass?.show_element;
  const level = dogClass?.level;
  const existingDeductions: IHerdingElementDeduction[] = result.herding?.element_deductions ?? [];

  const getAmount = (exerciseName: string): string => {
    const existing = existingDeductions.find((d) => d.element_name === exerciseName);
    return existing ? existing.points_deducted.toString() : "0";
  };

  if (course === Constants.dog_class_element.course_d) {
    const rows: IHerdingDeductionRow[] = HERDING_COURSE_D_ELEMENTS.map((ex, index) => ({
      id: `element-${index}`,
      label: ex.exercise,
      maxPoints: ex.points,
      amount: getAmount(ex.exercise),
    }));

    const fixedNames = HERDING_COURSE_D_ELEMENTS.map((ex) => ex.exercise);
    const optionalExisting = existingDeductions.filter((d) => !fixedNames.includes(d.element_name));

    for (let i = 0; i < HERDING_COURSE_D_OPTIONAL_ELEMENT_COUNT; i++) {
      const existing = optionalExisting[i];

      rows.push({
        id: `optional-${i}`,
        label: existing?.element_name ?? "",
        maxPoints: HERDING_COURSE_D_OPTIONAL_ELEMENT_POINTS,
        amount: existing ? existing.points_deducted.toString() : "0",
        editableLabel: true,
      });
    }

    return rows;
  }

  const exercises: IHerdingDeductionExercise[] | undefined =
    course && level ? HerdingDeductions[course]?.[level] : undefined;

  if (!exercises?.length) {
    // Unknown/unconfigured course-level combination - fall back to a single
    // total deduction entry so the secretary is never blocked from scoring.
    const totalExisting = existingDeductions[0];
    return [{
      id: "fallback",
      label: "Points Deducted",
      amount: totalExisting ? totalExisting.points_deducted.toString() : (result.herding?.deducted_points?.toString() ?? "0"),
    }];
  }

  return exercises.map((ex, index) => ({
    id: `element-${index}`,
    label: ex.exercise,
    maxPoints: ex.points,
    amount: getAmount(ex.exercise),
  }));
}

const HerdingResults: React.FC<IProps> = ({ show, showType, result, onChange }) => {
  const [score] = useState<number>(result.herding?.score ?? 0);
  const [resultType, setResultType] = useState<IOption>({ id: Constants.score_result_type.qualified, value: "Qualified" });

  const [startedAt, setStartedAt] = useState<string>(result.herding?.started_at ?? "12:00:00");
  const [finishedAt, setFinishedAt] = useState<string>(result.herding?.finished_at ?? "12:00:00");
  const [deductionRows, setDeductionRows] = useState<IHerdingDeductionRow[]>(() => buildExerciseRows(result));
  const [misbehaviourDeduction, setMisbehaviourDeduction] = useState<number>(result.herding?.misbehaviour_points ?? 0);
  const [comment, setComment] = useState<string>(result.herding?.comments ?? "");

  const [loaded, setLoaded] = useState<boolean>(false);

  const isSimpleHerdingResult = useMemo(() => (
    showType === Constants.show_type.pre_trial_herding ||
    showType === Constants.show_type.instinct_test_herding
  ), [showType]);

  const resultOptions:IOption[] = useMemo(() => {
    if (isSimpleHerdingResult) {
      return [
        { id: Constants.score_result_type.qualified, value: "Pass" },
        { id: Constants.score_result_type.not_qualified, value: "Fail" },
        { id: Constants.score_result_type.absent, value: "Absent" }
      ];
    }

    return [
      { id: Constants.score_result_type.qualified, value: "Qualified" },
      { id: Constants.score_result_type.not_qualified, value: "NQ"},
      { id: Constants.score_result_type.absent, value: "Absent" }
    ];
  }, [isSimpleHerdingResult]);

  const [hasChanged, setHasChanged] = useState<boolean>(false);

  const [scores, setScores] = useState<IScore[]>([]);

  useEffect(() => {
    if (!loaded) {
      if (result?.herding) {
        const { result_type } = result.herding;

        for (let ro in resultOptions) {
          if (resultOptions[ro].id === result_type) {
            setResultType(resultOptions[ro]);
            break;
          }
        }
      }

      if (show) {
        apiService.getScoresGroupPlacements(
          show.event_id,
          result._id,
          {
            resultField: "herding",
            secondsTimeField: "herding.elapsed_time",
          }
        )
          .then((scores) => {
            setScores(scores);
          })
          .catch(() => {})
      }

      setLoaded(true);
    }
  }, [loaded, result, show, resultOptions]);

  const exerciseDeductionTotal = useMemo(() => (
    deductionRows.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0)
  ), [deductionRows]);

  const buildElementDeductionsPayload = useCallback((): IHerdingElementDeduction[] => {
    return deductionRows
      .filter((row) => row.label.trim() !== "")
      .map((row) => ({
        element_name: row.label,
        points_deducted: parseFloat(row.amount) || 0,
      }));
  }, [deductionRows]);

  useEffect(() => {
    if (hasChanged) {
      if (isSimpleHerdingResult) {
        onChange({
          result_type: resultType.id,
          score: resultType.id === Constants.score_result_type.qualified ? 100 : 0,
          started_at: "00:00:00",
          finished_at: "00:00:00",
          elapsed_time: 0,
          element_deductions: [],
          deducted_points: 0,
          misbehaviour_points: 0,
          total_points_deducted: 0,
          comments: "",
        });

        setHasChanged(false);
        return;
      }

      // Absent: the exercise/deduction fields are hidden (no run happened),
      // so they're left at their untouched defaults of 0 - without this,
      // "100 - 0" would save a full score of 100 for a dog that never ran.
      if (resultType.id === Constants.score_result_type.absent) {
        onChange({
          result_type: resultType.id,
          score: undefined,
          started_at: "00:00:00",
          finished_at: "00:00:00",
          elapsed_time: 0,
          element_deductions: [],
          deducted_points: 0,
          misbehaviour_points: 0,
          total_points_deducted: 0,
          comments: comment,
        });

        setHasChanged(false);
        return;
      }

      const elementDeductions = buildElementDeductionsPayload();
      const totalDeducted = exerciseDeductionTotal + misbehaviourDeduction;

      onChange({
        score: 100 - totalDeducted,
        result_type: resultType.id,
        started_at: startedAt,
        finished_at: finishedAt,
        elapsed_time: getTimeDifferenceInSeconds(startedAt, finishedAt),
        element_deductions: elementDeductions,
        deducted_points: exerciseDeductionTotal,
        misbehaviour_points: misbehaviourDeduction,
        total_points_deducted: totalDeducted,
        comments: comment,
      });

      setHasChanged(false);
    }
  }, [hasChanged, result, resultType.id, startedAt, finishedAt, exerciseDeductionTotal, misbehaviourDeduction, comment, onChange, isSimpleHerdingResult, buildElementDeductionsPayload]);

  const calculateScore = useCallback(() => {
    return 100 - exerciseDeductionTotal - misbehaviourDeduction;
  }, [exerciseDeductionTotal, misbehaviourDeduction]);

  const calculatePlacement = useCallback((): number | undefined => {
    return calculateNewPlacement({
      showType: "herding",
      result,
      scores,
      calculateScore,
      startedAt,
      finishedAt,
    });
  }, [result, calculateScore, startedAt, finishedAt, scores]);

  function getTimeDifferenceInSeconds(startTime: string, endTime: string): number {
    const _start = moment(startTime, "HH:mm:ss");
    const _end = moment(endTime, "HH:mm:ss");

    return _end.diff(_start, "seconds");
  }

  function formatSecondsToTime() {
    const seconds = getTimeDifferenceInSeconds(startedAt, finishedAt);

    if (seconds <= 0) {
      return "00.00";
    }

    const duration = moment.duration(seconds, "seconds");

    if (duration.hours() > 0) {
      return moment.utc(duration.asMilliseconds()).format("HH.mm.ss");
    } else {
      return moment.utc(duration.asMilliseconds()).format("mm.ss");
    }
  }

  const updateDeductionAmount = (id: string, value: string) => {
    const normalized = normalizeDecimalInput(value);
    setDeductionRows((prev) => prev.map((row) => row.id === id ? { ...row, amount: normalized } : row));
    setHasChanged(true);
  }

  const updateDeductionLabel = (id: string, value: string) => {
    setDeductionRows((prev) => prev.map((row) => row.id === id ? { ...row, label: value } : row));
    setHasChanged(true);
  }

  const handleMisbehaviourChange = (event: ChangeEvent<HTMLInputElement>) => {
    setMisbehaviourDeduction(Number(event.target.value));
    setHasChanged(true);
  }

  const handleCommentChange = (event: ChangeEvent<HTMLInputElement>) => {
    setComment(event.target.value);
    setHasChanged(true);
  }

  const dogClass = result.dog_class;

  return (
    <div className="HerdingResults">
      <Dropdown value={resultType} label="Result" onChange={(_e, value: IOption) => { setResultType(value); setHasChanged(true); }} options={resultOptions} placeholder="" />
      {!isSimpleHerdingResult && [Constants.score_result_type.qualified, Constants.score_result_type.not_qualified].includes(resultType.id) &&
        <>
          <TimeInput
            label="Start time"
            value={startedAt}
            maxDetail="second"
            clockIcon={null}
            onChange={(val) => {
              setStartedAt(val as string);
              setHasChanged(true);
            }}
          />
          <TimeInput
            label="Finish time"
            value={finishedAt}
            maxDetail="second"
            clockIcon={null}
            onChange={(val) => {
              setFinishedAt(val as string);
              setHasChanged(true);
            }}
          />
          <Input disabled value={formatSecondsToTime()} label="Elapsed time" placeholder="" defaultValue="" />

          <div className="exerciseDeductions">
            {deductionRows.map((row, index) => (
              <div className="exerciseRow" key={row.id}>
                {row.editableLabel && (
                  <Input
                    type="text"
                    onChange={(e: any) => { updateDeductionLabel(row.id, e.target.value); }}
                    label={`Optional Element ${index - (deductionRows.length - HERDING_COURSE_D_OPTIONAL_ELEMENT_COUNT) + 1} Name`}
                    placeholder="Enter element name"
                    defaultValue={row.label}
                  />
                )}
                <Input
                  type="number"
                  min={0}
                  max={row.maxPoints}
                  onChange={(e: any) => { updateDeductionAmount(row.id, e.target.value); }}
                  label={row.editableLabel
                    ? `Points Deducted${row.maxPoints ? ` (Max ${row.maxPoints})` : ""}`
                    : `${index + 1}. ${row.label}${row.maxPoints ? ` (Max ${row.maxPoints})` : ""}`}
                  defaultValue={row.amount}
                />
              </div>
            ))}
          </div>

          <Input type="number" min={0} max={100} onChange={handleMisbehaviourChange} label="Deductions for Misbehaviour" defaultValue={misbehaviourDeduction.toString()} />
          <Input type="text" onChange={handleCommentChange} label="Comments" placeholder="Enter the comment" defaultValue={comment} />

          <Input type="number" disabled  label="Score" placeholder="Score (maximum is 100)" value={calculateScore()} />

          <p>{`Result in: ${dogClass?.show_element} - ${dogClass?.level} - ${dogClass?.section}`}</p>
          <Input type="number" disabled label="Placemennt" placeholder="Enter run time to get placement" value={calculatePlacement()} />
        </>
      }
    </div>
  );
};

export default HerdingResults;
